import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../Config';
import keepaiLogo from '../resource/keepaiLogo.png';
import '../css/style/admin.css';

const getAuth = () => {
    const s = localStorage.getItem('user');
    return s ? JSON.parse(s) : null;
};

const fmt = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const isExpired = (d) => d && new Date(d) < new Date();

const PRODUCT_OPTIONS = [
    { value: 'chatgpt', label: 'ChatGPT Plus' },
    { value: 'claude',  label: 'Claude Pro' },
    { value: 'codex',   label: 'GPT-5.3 Codex' },
    { value: 'netflix', label: 'Netflix' },
    { value: 'tving',   label: 'Tiving' },
];

const Admin = () => {
    const navigate = useNavigate();
    const user = getAuth();
    const authHeader = { Authorization: `Bearer ${user?.token}` };

    // ── 탭 ──
    const [activeTab, setActiveTab] = useState('users');

    // ── 유저 관리 ──
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [search, setSearch] = useState('');

    // ── 채팅방 관리 ──
    const [rooms, setRooms] = useState([]);
    const [roomsLoading, setRoomsLoading] = useState(false);
    const [showRoomModal, setShowRoomModal] = useState(false);
    const [roomForm, setRoomForm] = useState({ name: '', product_type: 'chatgpt', max_members: '4' });
    const [roomSaving, setRoomSaving] = useState(false);
    const [addMemberInputs, setAddMemberInputs] = useState({});
    const [memberSaving, setMemberSaving] = useState(null);

    useEffect(() => {
        if (!user?.token || user?.role !== 'R') { navigate('/login'); return; }
        fetch(`${BASE_URL}/admin/users`, { headers: authHeader })
            .then(r => r.json())
            .then(data => setUsers(Array.isArray(data) ? data : []))
            .catch(err => console.error('유저 목록 오류:', err))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line

    const fetchRooms = () => {
        setRoomsLoading(true);
        fetch(`${BASE_URL}/admin/rooms`, { headers: authHeader })
            .then(r => r.json())
            .then(data => setRooms(Array.isArray(data) ? data : []))
            .catch(err => console.error('채팅방 목록 오류:', err))
            .finally(() => setRoomsLoading(false));
    };

    useEffect(() => {
        if (activeTab === 'rooms') fetchRooms();
    }, [activeTab]); // eslint-disable-line

    const handleRoleChange = async (username, newRole) => {
        setSaving(username);
        try {
            const res = await fetch(`${BASE_URL}/admin/users/${encodeURIComponent(username)}/role`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeader },
                body: JSON.stringify({ role: newRole }),
            });
            if (res.ok) setUsers(prev => prev.map(u => u.USERNAME === username ? { ...u, ROLE: newRole } : u));
        } catch (err) { console.error('ROLE 변경 오류:', err); }
        finally { setSaving(null); }
    };

    const handleCreateRoom = async () => {
        if (!roomForm.name.trim()) return;
        setRoomSaving(true);
        try {
            const res = await fetch(`${BASE_URL}/admin/rooms`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader },
                body: JSON.stringify(roomForm),
            });
            if (res.ok) {
                setShowRoomModal(false);
                setRoomForm({ name: '', product_type: 'chatgpt', max_members: '4' });
                fetchRooms();
            }
        } catch (err) { console.error('방 생성 오류:', err); }
        finally { setRoomSaving(false); }
    };

    const handleDeleteRoom = async (roomId) => {
        if (!window.confirm('채팅방을 삭제하면 모든 메시지가 삭제됩니다. 계속할까요?')) return;
        try {
            await fetch(`${BASE_URL}/admin/rooms/${roomId}`, { method: 'DELETE', headers: authHeader });
            fetchRooms();
        } catch (err) { console.error('방 삭제 오류:', err); }
    };

    const handleAddMember = async (roomId) => {
        const username = (addMemberInputs[roomId] || '').trim();
        if (!username) return;
        setMemberSaving(`${roomId}:add`);
        try {
            const res = await fetch(`${BASE_URL}/admin/rooms/${roomId}/members`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader },
                body: JSON.stringify({ username }),
            });
            const data = await res.json();
            if (res.ok) {
                setAddMemberInputs(prev => ({ ...prev, [roomId]: '' }));
                fetchRooms();
            } else {
                alert(data.error || '멤버 추가 실패');
            }
        } catch (err) { console.error('멤버 추가 오류:', err); }
        finally { setMemberSaving(null); }
    };

    const handleRemoveMember = async (roomId, username) => {
        setMemberSaving(`${roomId}:${username}`);
        try {
            await fetch(`${BASE_URL}/admin/rooms/${roomId}/members/${encodeURIComponent(username)}`, {
                method: 'DELETE', headers: authHeader,
            });
            fetchRooms();
        } catch (err) { console.error('멤버 제거 오류:', err); }
        finally { setMemberSaving(null); }
    };

    const filtered = users.filter(u =>
        (u.NAME || '').includes(search) || (u.PHONE || '').includes(search)
    );

    return (
        <div className="adm-wrap">
            <header className="adm-hdr">
                <img src={keepaiLogo} alt="KeepAI" className="adm-logo" onClick={() => navigate('/')} />
                <span className="adm-hdr-title">관리자 페이지</span>
                <button className="adm-logout" onClick={() => { localStorage.removeItem('user'); navigate('/login'); }}>
                    로그아웃
                </button>
            </header>

            <main className="adm-main">
                {/* 탭 */}
                <div className="adm-tabs">
                    <button className={`adm-tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
                        유저 관리
                    </button>
                    <button className={`adm-tab ${activeTab === 'rooms' ? 'active' : ''}`} onClick={() => setActiveTab('rooms')}>
                        채팅방 관리
                    </button>
                </div>

                {/* ── 유저 관리 탭 ── */}
                {activeTab === 'users' && (
                    <div className="adm-card">
                        <div className="adm-card-hdr">
                            <div className="adm-card-left">
                                <h2>유저 목록</h2>
                                <span className="adm-count">총 {users.length}명</span>
                            </div>
                            <input
                                className="adm-search"
                                placeholder="이름 / 전화번호 검색"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        {loading ? (
                            <div className="adm-loading">로딩 중...</div>
                        ) : (
                            <div className="adm-table-wrap">
                                <table className="adm-table">
                                    <thead>
                                        <tr>
                                            <th>아이디</th><th>이름</th><th>전화번호</th>
                                            <th>가입일</th><th>권한</th><th>결제일</th>
                                            <th>만료일</th><th>총 토큰</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.length === 0 ? (
                                            <tr><td colSpan="8" className="adm-empty">검색 결과 없음</td></tr>
                                        ) : filtered.map(u => (
                                            <tr key={u.USERNAME} className={u.ROLE === 'R' ? 'row-admin' : ''}>
                                                <td className="td-id">{u.USERNAME}</td>
                                                <td>{u.NAME || '-'}</td>
                                                <td className="td-phone">{u.PHONE || '-'}</td>
                                                <td>{fmt(u.REG_DTM)}</td>
                                                <td>
                                                    <select
                                                        className={`role-sel role-${u.ROLE}`}
                                                        value={u.ROLE}
                                                        disabled={saving === u.USERNAME}
                                                        onChange={e => handleRoleChange(u.USERNAME, e.target.value)}
                                                    >
                                                        <option value="N">N · 미인증</option>
                                                        <option value="Y">Y · 유료</option>
                                                        <option value="R">R · 관리자</option>
                                                    </select>
                                                </td>
                                                <td>{fmt(u.PAYMENT_DATE)}</td>
                                                <td className={isExpired(u.EXPIRY_DATE) ? 'td-expired' : ''}>{fmt(u.EXPIRY_DATE)}</td>
                                                <td className="td-tokens">{Number(u.TOTAL_TOKENS || 0).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* ── 채팅방 관리 탭 ── */}
                {activeTab === 'rooms' && (
                    <div className="adm-card">
                        <div className="adm-card-hdr">
                            <div className="adm-card-left">
                                <h2>채팅방 목록</h2>
                                <span className="adm-count">총 {rooms.length}개</span>
                            </div>
                            <button className="adm-create-btn" onClick={() => setShowRoomModal(true)}>+ 방 만들기</button>
                        </div>

                        {roomsLoading ? (
                            <div className="adm-loading">로딩 중...</div>
                        ) : rooms.length === 0 ? (
                            <div className="adm-loading">채팅방이 없습니다. 방을 만들어주세요.</div>
                        ) : (
                            <div className="adm-rooms-grid">
                                {rooms.map(room => (
                                    <div key={room.ROOM_ID} className="adm-room-card">
                                        <div className="adm-room-top">
                                            <div>
                                                <div className="adm-room-name">{room.NAME}</div>
                                                <div className="adm-room-meta">
                                                    <span className={`adm-product-badge adm-product-${room.PRODUCT_TYPE}`}>
                                                        {PRODUCT_OPTIONS.find(p => p.value === room.PRODUCT_TYPE)?.label || room.PRODUCT_TYPE}
                                                    </span>
                                                    <span className="adm-room-cnt">{room.MEMBERS?.length || 0} / {room.MAX_MEMBERS}명</span>
                                                </div>
                                            </div>
                                            <button className="adm-room-del" onClick={() => handleDeleteRoom(room.ROOM_ID)}>삭제</button>
                                        </div>

                                        <div className="adm-member-list">
                                            {!room.MEMBERS?.length
                                                ? <span className="adm-no-member">멤버 없음</span>
                                                : room.MEMBERS.map(m => (
                                                    <div key={m} className="adm-member-item">
                                                        <span>{m}</span>
                                                        <button
                                                            className="adm-member-del"
                                                            disabled={memberSaving === `${room.ROOM_ID}:${m}`}
                                                            onClick={() => handleRemoveMember(room.ROOM_ID, m)}
                                                        >✕</button>
                                                    </div>
                                                ))
                                            }
                                        </div>

                                        {(room.MEMBERS?.length || 0) < room.MAX_MEMBERS && (
                                            <div className="adm-add-member">
                                                <input
                                                    className="adm-member-input"
                                                    placeholder="추가할 아이디 입력"
                                                    value={addMemberInputs[room.ROOM_ID] || ''}
                                                    onChange={e => setAddMemberInputs(prev => ({ ...prev, [room.ROOM_ID]: e.target.value }))}
                                                    onKeyDown={e => { if (e.key === 'Enter') handleAddMember(room.ROOM_ID); }}
                                                />
                                                <button
                                                    className="adm-member-add-btn"
                                                    disabled={memberSaving === `${room.ROOM_ID}:add`}
                                                    onClick={() => handleAddMember(room.ROOM_ID)}
                                                >추가</button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* 방 만들기 모달 */}
            {showRoomModal && (
                <div className="adm-modal-backdrop" onClick={() => setShowRoomModal(false)}>
                    <div className="adm-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="adm-modal-title">채팅방 만들기</h3>
                        <div className="adm-modal-field">
                            <label>방 이름</label>
                            <input
                                className="adm-modal-input"
                                placeholder="예: 넷플릭스 공유방 1"
                                value={roomForm.name}
                                onChange={e => setRoomForm(prev => ({ ...prev, name: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') handleCreateRoom(); }}
                            />
                        </div>
                        <div className="adm-modal-field">
                            <label>상품</label>
                            <select
                                className="adm-modal-input"
                                value={roomForm.product_type}
                                onChange={e => setRoomForm(prev => ({ ...prev, product_type: e.target.value }))}
                            >
                                {PRODUCT_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                        </div>
                        <div className="adm-modal-field">
                            <label>최대 인원</label>
                            <select
                                className="adm-modal-input"
                                value={roomForm.max_members}
                                onChange={e => setRoomForm(prev => ({ ...prev, max_members: e.target.value }))}
                            >
                                {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}명</option>)}
                            </select>
                        </div>
                        <div className="adm-modal-btns">
                            <button className="adm-modal-cancel" onClick={() => setShowRoomModal(false)}>취소</button>
                            <button className="adm-modal-confirm" onClick={handleCreateRoom} disabled={roomSaving}>
                                {roomSaving ? '생성 중...' : '만들기'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admin;